import type { FunctionComponent } from "react";
import { useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import classNames from "classnames";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { Autocomplete, DotsLoader } from "@similarweb/ui-components";

import categoryService from "app/common/services/categoryService";
import type { ECategoryType, ICategory, IFlattenedCategory } from "app/common/services/categoryService.types";
import { NoResultsContainer } from "pro-features/components/AutocompleteKeywords/styles/AutocompleteKeywordStyles";
import { CustomCategoriesWizard } from "app/components/customCategoriesWizard/CustomCategoriesWizard";
import { i18nFilter } from "app/filters/i18nFilter";

import { AutocompleteWebCategoriesTabs } from "./AutocompleteWebCategoriesTabs";
import type { IAutocompleteWebCategoriesProps } from "./types";
import { ETabs } from "./types";
import {
    getPartnerTypeLists,
    renderItemCreator,
    renderItemsClick as createRenderItemsClick,
    useAutoCompleteClick,
    ScrollAreaWrap,
    createListItemFactory,
} from "./utils";

const i18n = i18nFilter();
const NoResultTitle = "query.bar.no.result.title";

const AutocompleteStyled = styled(Autocomplete)`
    width: 100%;
`;

const NoCategoriesResults = (props: { title: string }) => {
    return (
        <NoResultsContainer>
            <SWReactIcons
                iconName={"no-search-results"}
                size={"sm"}
            />
            <span>{props.title}</span>
        </NoResultsContainer>
    );
};

export const AutocompleteWebCategories: FunctionComponent<IAutocompleteWebCategoriesProps> = ({
    selectedValue,
    onClick,
    autocompleteProps = {},
    className,
    defaultSelectedTab = ETabs.PRE_DEFINED,
    showCustomCategoryEmptyState,
    onCreateCustomCategoryClick,
    onClearSearch,
    showOnlyCategories = false,
    showOnlyCustom = false,
    categoryModalProps,
    isPartnerPage,
    onCloseCustomCategoriesModal,
    customResolveItemIcon,
    disabledCategories = [],
    isCompare = false,
    hasSelectedCategoryWithSegments,
}) => {
    const autocompleteRef = useRef(null);
    useAutoCompleteClick(autocompleteRef);
    const [showCustomCategoriesWizard, setShowCustomCategoriesWizard] = useState(false);
    const [customCategoryName, setCustomCategoryName] = useState<string | null>(null);
    const [selectedTab, setSelectedTab] = useState<ETabs>(defaultSelectedTab);
    const queryRef = useRef("");

    const allCategories: IFlattenedCategory[] = categoryService.getFlattenedCategoriesList(true);

    const { params } = useCurrentStateAndParams();

    const renderItemsClick = createRenderItemsClick(autocompleteRef, onClick, queryRef.current);

    const getDataRenderItemsTabs = (query: string) => {
        queryRef.current = query;
        const hasQuery = typeof query === "string" && query !== "";
        if (!hasQuery && selectedValue) {
            onClearSearch?.();
        }
        const queryResults = hasQuery
            ? allCategories.filter(({ text, id }) => {
                  const lowerCaseQuery = query.trim().toLowerCase();
                  const textAndId = `${text}${id} `.toLowerCase();
                  return textAndId.includes(lowerCaseQuery) || textAndId.replace(/_/g, " ").includes(lowerCaseQuery);
              })
            : allCategories;

        return queryResults.map(renderItemCreator(renderItemsClick));
    };

    const createListItem = createListItemFactory(
        renderItemsClick,
        setCustomCategoryName,
        setShowCustomCategoriesWizard,
        customResolveItemIcon,
        params,
        disabledCategories,
        hasSelectedCategoryWithSegments,
    );

    const renderWithoutTabs = ({ listItems, query }: FixmeType) => {
        let list = listItems;

        if (list.length === 0 && query?.length > 0 && !selectedValue) {
            return <NoCategoriesResults title={i18n(NoResultTitle)} />;
        }

        if (isPartnerPage) {
            list = getPartnerTypeLists(listItems);
        }

        if (showOnlyCategories && query?.length >= 0) {
            const categories: ICategory[] = list
                .filter((listItem: FixmeType) => !listItem.domains)
                .map(createListItem(query?.length > 0));
            return categories.length === 0 ? null : (
                <div className="ListItemsContainer">
                    <ScrollAreaWrap>{categories}</ScrollAreaWrap>
                </div>
            );
        }

        if (showOnlyCustom) {
            const customCategories: ICategory[] = list
                .filter((listItem: FixmeType) => listItem.domains)
                .map(createListItem(query?.length > 0));
            return customCategories.length === 0 ? (
                <NoCategoriesResults title={i18n(NoResultTitle)} />
            ) : (
                <div className="ListItemsContainer">
                    <ScrollAreaWrap>{customCategories}</ScrollAreaWrap>
                </div>
            );
        }
    };
    const renderItemsTabs = ({ listItems, query }: FixmeType) => {
        return (
            <AutocompleteWebCategoriesTabs
                listItems={listItems}
                query={query}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                createListItem={createListItem}
                showCustomCategoryEmptyState={showCustomCategoryEmptyState}
                onCreateCustomCategoryClick={onCreateCustomCategoryClick}
                customResolveItemIcon={customResolveItemIcon}
            />
        );
    };

    const renderItems = showOnlyCategories || showOnlyCustom ? renderWithoutTabs : renderItemsTabs;

    return (
        <>
            <AutocompleteStyled
                className={classNames("AutocompleteWithTabs", className)}
                getListItems={getDataRenderItemsTabs}
                renderItems={renderItems}
                preventTruncateUnlessForced={true}
                ref={autocompleteRef}
                loadingComponent={<DotsLoader />}
                floating={true}
                debounce={250}
                placeholder="Start typing here..."
                selectedValue={selectedValue?.text}
                {...autocompleteProps}
            />
            <CustomCategoriesWizard
                isOpen={showCustomCategoriesWizard}
                onClose={() => {
                    setShowCustomCategoriesWizard(false);
                    onCloseCustomCategoriesModal?.();
                }}
                wizardProps={{
                    initialCategoryType: categoryModalProps?.categoryType as ECategoryType,
                    // @ts-ignore FIXME: strict migration - Type 'string | null' is not assignable to type 'string | undefined' — local state initializes as null; wizard prop is optional and treats null/undefined identically
                    customCategoryName: customCategoryName,
                    onSave: () => {
                        setShowCustomCategoriesWizard(false);
                    },
                }}
            />
        </>
    );
};
